import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { zipSync } from 'fflate';
import {
  downloadAllIncidenceActionsArchive,
  getAllMeetingEvidences
} from '../../API/apiService';
import EvidencePreviewModal from '../../components/evidencePreviewModal';
import { buildProtectedImageUrl, fetchWithAuth } from '../../utils/secureMedia';
import './style.scss';

const EVIDENCE_LABELS = {
  bitacora: 'Bitácora',
  ficha_metodologica: 'Ficha metodológica',
  asistencia: 'Listado de asistencia'
};

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function evidenceLabel(image, index) {
  return EVIDENCE_LABELS[normalizeText(image?.name)] || `Evidencia ${index + 1}`;
}

function evidenceExtension(image) {
  const type = normalizeText(image?.type);
  if (type === 'jpeg') return 'jpg';
  return ['pdf', 'png', 'jpg', 'webp'].includes(type) ? type : 'bin';
}

function safeFileSegment(value, fallback) {
  const normalized = String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
}

function incidenceEvidenceFileBase(action, image, index) {
  const prefixes = {
    bitacora: 'Bit',
    ficha_metodologica: 'Fic',
    asistencia: 'As'
  };
  const prefix = prefixes[normalizeText(image?.name)] || `Evidencia${index + 1}`;
  const municipalityId = String(action?.municipalityId || '').replace(/\D/g, '') || 'SinComuna';
  const dateMatch = String(action?.meetingDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dateCode = dateMatch
    ? `${dateMatch[3]}${dateMatch[2]}${dateMatch[1].slice(-2)}`
    : 'SinFecha';
  return `${prefix}C${municipalityId}_${dateCode}_AccInc`;
}

function formatDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : 'Fecha no disponible';
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function fetchEvidenceFile(image) {
  const response = await fetchWithAuth(buildProtectedImageUrl(image?.id, false));
  if (!response.ok) throw new Error('EVIDENCE_DOWNLOAD_FAILED');
  return new Uint8Array(await response.arrayBuffer());
}

export default function SuperadminIncidenceEvidences() {
  const [actions, setActions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    let active = true;
    getAllMeetingEvidences()
      .then(records => {
        if (!active) return;
        const incidenceActions = (Array.isArray(records) ? records : [])
          .filter(record => normalizeText(record?.sourceType) === 'incidence_action')
          .sort((first, second) =>
            String(second?.meetingDate || '').localeCompare(String(first?.meetingDate || ''))
          );
        setActions(incidenceActions);
      })
      .catch(() => {
        if (active) setError('No fue posible cargar las evidencias de incidencia.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const filteredActions = useMemo(() => {
    const term = normalizeText(search);
    if (!term) return actions;
    return actions.filter(action => normalizeText([
      action?.meetingName,
      action?.mediatorName,
      action?.groupCode,
      action?.schoolName,
      action?.groupDescription
    ].join(' ')).includes(term));
  }, [actions, search]);

  const selectedAction = preview
    ? actions.find(action => Number(action.id) === Number(preview.actionId))
    : null;
  const selectedImages = Array.isArray(selectedAction?.images) ? selectedAction.images : [];
  const selectedImage = selectedImages[preview?.imageIndex] || null;

  async function downloadOne(action, image, index) {
    const downloadKey = `${action.id}-${image.id}`;
    setDownloading(downloadKey);
    try {
      const bytes = await fetchEvidenceFile(image);
      const fileName = `${incidenceEvidenceFileBase(action, image, index)}.${evidenceExtension(image)}`;
      downloadBlob(new Blob([bytes]), fileName);
    } catch (downloadError) {
      toast('No fue posible descargar la evidencia.', { type: 'error' });
    } finally {
      setDownloading('');
    }
  }

  async function downloadAll(action) {
    const images = Array.isArray(action?.images) ? action.images : [];
    if (!images.length) return;
    const downloadKey = `action-${action.id}`;
    setDownloading(downloadKey);
    try {
      const files = {};
      for (const [index, image] of images.entries()) {
        const baseName = incidenceEvidenceFileBase(action, image, index);
        let fileName = `${baseName}.${evidenceExtension(image)}`;
        if (Object.prototype.hasOwnProperty.call(files, fileName)) {
          fileName = `${baseName}_${index + 1}.${evidenceExtension(image)}`;
        }
        files[fileName] = await fetchEvidenceFile(image);
      }
      const archiveName = `${safeFileSegment(action.meetingName, `accion_incidencia_${action.id}`)}_evidencias.zip`;
      downloadBlob(new Blob([zipSync(files, { level: 0 })], { type: 'application/zip' }), archiveName);
      toast('Descarga de evidencias iniciada.', { type: 'success' });
    } catch (downloadError) {
      toast('No fue posible descargar las evidencias de la acción.', { type: 'error' });
    } finally {
      setDownloading('');
    }
  }

  async function downloadEveryAction() {
    if (!actions.length || downloading) return;
    setDownloading('all-actions');
    try {
      const archive = await downloadAllIncidenceActionsArchive();
      downloadBlob(archive, 'acciones_incidencia_evidencias.zip');
      toast('Descarga de todas las acciones iniciada.', { type: 'success' });
    } catch (downloadError) {
      toast('No fue posible generar el archivo de acciones de incidencia.', { type: 'error' });
    } finally {
      setDownloading('');
    }
  }

  function movePreview(step) {
    if (!preview || selectedImages.length < 2) return;
    setPreview(current => ({
      ...current,
      imageIndex: (current.imageIndex + step + selectedImages.length) % selectedImages.length
    }));
  }

  return (
    <section className='superadmin-incidence-evidences'>
      <EvidencePreviewModal
        open={Boolean(selectedImage)}
        sourceUrl={selectedImage ? buildProtectedImageUrl(selectedImage.id, false) : ''}
        fileType={normalizeText(selectedImage?.type) === 'pdf' ? 'pdf' : 'image'}
        fileName={selectedImage ? evidenceLabel(selectedImage, preview.imageIndex) : ''}
        currentIndex={preview?.imageIndex ?? 0}
        totalItems={selectedImages.length}
        onPrevious={selectedImages.length > 1 ? () => movePreview(-1) : undefined}
        onNext={selectedImages.length > 1 ? () => movePreview(1) : undefined}
        onClose={() => setPreview(null)}
        closeOnBackdrop
      />

      <div className='superadmin-incidence-evidences__content'>
        <Link className='superadmin-incidence-evidences__back' to='/superadmin'>
          <span className='material-symbols-rounded'>arrow_back</span>
          Volver al panel de superadmin
        </Link>

        <header className='superadmin-incidence-evidences__heading'>
          <div>
            <p>EVIDENCIAS</p>
            <h1>Acciones de incidencia</h1>
            <span>Consulta, visualiza y descarga los soportes registrados.</span>
          </div>
          <div className='superadmin-incidence-evidences__heading-actions'>
            <strong>{filteredActions.length} acciones</strong>
            <button
              type='button'
              onClick={downloadEveryAction}
              disabled={!actions.length || Boolean(downloading)}>
              <span className='material-symbols-rounded'>folder_zip</span>
              {downloading === 'all-actions' ? 'Preparando...' : 'Descargar todas las acciones'}
            </button>
          </div>
        </header>

        <label className='superadmin-incidence-evidences__search'>
          <span className='material-symbols-rounded'>search</span>
          <input
            type='search'
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder='Buscar acción, mediador, grupo o institución'
          />
        </label>

        {loading && <p className='superadmin-incidence-evidences__state'>Cargando evidencias...</p>}
        {error && <p className='superadmin-incidence-evidences__error' role='alert'>{error}</p>}
        {!loading && !error && filteredActions.length === 0 && (
          <p className='superadmin-incidence-evidences__state'>
            {search ? 'No hay acciones que coincidan con la búsqueda.' : 'Aún no hay evidencias de acciones de incidencia.'}
          </p>
        )}

        <div className='superadmin-incidence-evidences__list'>
          {filteredActions.map(action => {
            const images = Array.isArray(action?.images) ? action.images : [];
            return (
              <article key={action.id}>
                <div className='superadmin-incidence-evidences__summary'>
                  <div>
                    <h2>{action.meetingName || 'Acción de incidencia'}</h2>
                    <p>{formatDate(action.meetingDate)} · {action.groupCode || 'Sin grupo'}</p>
                    <small>{action.mediatorName || 'Sin mediador'}{action.schoolName ? ` · ${action.schoolName}` : ''}</small>
                  </div>
                  <button
                    type='button'
                    onClick={() => downloadAll(action)}
                    disabled={!images.length || Boolean(downloading)}>
                    <span className='material-symbols-rounded'>folder_zip</span>
                    {downloading === `action-${action.id}` ? 'Preparando...' : 'Descargar todas'}
                  </button>
                </div>

                <div className='superadmin-incidence-evidences__files'>
                  {images.map((image, index) => (
                    <div key={image.id}>
                      <span className='material-symbols-rounded'>
                        {normalizeText(image?.type) === 'pdf' ? 'picture_as_pdf' : 'image'}
                      </span>
                      <strong>{evidenceLabel(image, index)}</strong>
                      <button
                        type='button'
                        onClick={() => setPreview({ actionId: action.id, imageIndex: index })}
                        aria-label={`Visualizar ${evidenceLabel(image, index)}`}>
                        <span className='material-symbols-rounded'>visibility</span>
                        Ver
                      </button>
                      <button
                        type='button'
                        onClick={() => downloadOne(action, image, index)}
                        disabled={Boolean(downloading)}
                        aria-label={`Descargar ${evidenceLabel(image, index)}`}>
                        <span className='material-symbols-rounded'>download</span>
                        {downloading === `${action.id}-${image.id}` ? 'Descargando...' : 'Descargar'}
                      </button>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
